import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen,
  Search,
  ChevronRight,
  ArrowLeft,
  Eye,
  Clock,
  Tag,
  FileText,
  HelpCircle,
  Lightbulb,
  Settings,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoriaIcons = {
  'Primeiros Passos': Lightbulb,
  'Como Usar': BookOpen,
  'Configurações': Settings,
  'Dúvidas Frequentes': HelpCircle,
  'default': FileText,
};

const BaseConhecimento = () => {
  const navigate = useNavigate();
  const { artigoId } = useParams();
  const [loading, setLoading] = useState(true);
  const [artigos, setArtigos] = useState([]);
  const [artigoAtual, setArtigoAtual] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');

  const token = localStorage.getItem('agrolink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchArtigos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/base-conhecimento`, { headers });
      setArtigos(res.data);
    } catch (error) {
      toast.error('Erro ao carregar artigos');
    } finally {
      setLoading(false);
    }
  };

  const fetchArtigo = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/base-conhecimento/${id}`, { headers });
      setArtigoAtual(res.data);
    } catch (error) {
      toast.error('Artigo não encontrado');
      navigate('/base-conhecimento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (artigoId) {
      fetchArtigo(artigoId);
    } else {
      fetchArtigos();
      setArtigoAtual(null);
    }
  }, [artigoId]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  // Extrair categorias únicas
  const categorias = [...new Set(artigos.map(a => a.categoria).filter(Boolean))];

  // Filtrar artigos
  const artigosFiltrados = artigos.filter(artigo => {
    const matchSearch = !searchTerm || 
      artigo.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artigo.conteudo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artigo.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchCategoria = !categoriaFiltro || artigo.categoria === categoriaFiltro;
    
    return matchSearch && matchCategoria;
  });

  // Agrupar por categoria
  const artigosPorCategoria = artigosFiltrados.reduce((acc, artigo) => {
    const cat = artigo.categoria || 'Outros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(artigo);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-10 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  // Visualização do artigo
  if (artigoAtual) {
    return (
      <div className="space-y-6 animate-fadeIn max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/base-conhecimento')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para Base de Conhecimento
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                {artigoAtual.categoria && (
                  <Badge variant="outline" className="mb-2">
                    {artigoAtual.categoria}
                  </Badge>
                )}
                <CardTitle className="text-2xl">{artigoAtual.titulo}</CardTitle>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDate(artigoAtual.created_at)}
              </div>
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {artigoAtual.visualizacoes} visualizações
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: artigoAtual.conteudo.replace(/\n/g, '<br/>') }}
            />
            
            {artigoAtual.tags?.length > 0 && (
              <div className="flex items-center gap-2 mt-6 pt-6 border-t">
                <Tag className="w-4 h-4 text-muted-foreground" />
                {artigoAtual.tags.map((tag, i) => (
                  <Badge key={i} variant="secondary">{tag}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Artigos relacionados */}
        {artigos.filter(a => a.categoria === artigoAtual.categoria && a.id !== artigoAtual.id).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Artigos Relacionados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {artigos
                  .filter(a => a.categoria === artigoAtual.categoria && a.id !== artigoAtual.id)
                  .slice(0, 4)
                  .map((artigo) => (
                    <div
                      key={artigo.id}
                      className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/base-conhecimento/${artigo.id}`)}
                    >
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{artigo.titulo}</span>
                      <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Lista de artigos
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <BookOpen className="w-8 h-8 text-primary" />
            Base de Conhecimento
          </h1>
          <p className="text-muted-foreground">Encontre respostas para suas dúvidas</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/suporte')}>
          <HelpCircle className="w-4 h-4 mr-2" />
          Precisa de Ajuda?
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar artigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={categoriaFiltro === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoriaFiltro('')}
              >
                Todos
              </Button>
              {categorias.map((cat) => (
                <Button
                  key={cat}
                  variant={categoriaFiltro === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoriaFiltro(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Artigos */}
      {artigosFiltrados.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum artigo encontrado</p>
            {searchTerm && (
              <Button variant="link" onClick={() => setSearchTerm('')}>
                Limpar busca
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        Object.entries(artigosPorCategoria).map(([categoria, artigos]) => {
          const Icon = categoriaIcons[categoria] || categoriaIcons.default;
          return (
            <div key={categoria}>
              <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-primary" />
                {categoria}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {artigos.map((artigo) => (
                  <Card
                    key={artigo.id}
                    className="cursor-pointer hover:shadow-md transition-all hover:border-primary/50"
                    onClick={() => navigate(`/base-conhecimento/${artigo.id}`)}
                  >
                    <CardContent className="p-4">
                      <h3 className="font-medium mb-2 line-clamp-2">{artigo.titulo}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {artigo.conteudo.replace(/<[^>]*>/g, '').substring(0, 100)}...
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {artigo.visualizacoes || 0}
                        </div>
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default BaseConhecimento;
